import { Box, Stack, Typography } from "@mui/material";
import PlanCard from "./PlanCard";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AddIcon from "@mui/icons-material/Add";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cancellationReasons, formatDate, planMap } from "../constant";
import { useModal } from "../containers/ModalProvider";
import StripePortalSessionService from "../../../Services/StripeService";
import { useGlobalContext } from "../../../Context/GlobalContext";

export const CurrentPlanCard = () => {
  // ====================== STATE
  const [eaiButtonLoading, setEaiButtonloading] = useState(false);

  // ====================== HOOKS
  const { openModal } = useModal();
  const { plan: userPlan } = useGlobalContext();
  const navigate = useNavigate();

  // ====================== VARIABLES
  const EAIPlan = userPlan?.eai;

  // ======================= Current Plan
  const EAICurrentPlan = EAIPlan?.plan;
  const isEAISubscribed = EAIPlan?.active;
  const isEAIGiven = EAIPlan?.freeSubscription;
  const EAICurrentPlanName = planMap[EAICurrentPlan || "Trial"];
  const currentEAIPlanEndDate = EAIPlan?.periodEnds
    ? formatDate(EAIPlan.periodEnds)
    : null;
  const isTrial = EAICurrentPlan === "Trial";
  const isEAIPaymentFailed = cancellationReasons.includes(
    EAIPlan?.cancellation_reason
  );
  const isEAICancellationRequested =
    isEAISubscribed &&
    EAIPlan?.cancellation_reason === "cancellation_requested";

  const isEAICancelManually = EAIPlan?.cancellation_reason === "Cancelled";

  const EAIHasStripeId = EAIPlan?.stripeCustomerId;

  // ====================== HANDLER
  const onHandleEAIPlanClick = async () => {
    // If user doesn't have a stripe id, navigate to checkout
    if (!EAIHasStripeId) {
      return navigate("/Billing/Checkout?plan=pro&type=annually");
    }
    // We don't want to show modal for trial and free tier because modal is for cancel subscription
    // trial and free plan doesn't require subscription cancellation
    // we also don't want to show modal if payment failed
    // if user has already requested for cancellation, we want to show resubscribe button with redirecting to portal
    if (
      EAICurrentPlan !== "Trial" &&
      EAICurrentPlan !== "Member" &&
      !isEAIPaymentFailed &&
      !isEAICancellationRequested &&
      !isEAICancelManually
    ) {
      return openModal();
    }
    setEaiButtonloading(true);
    try {
      const result = await StripePortalSessionService();
      const stripePortalSession = await result.json();
      if (stripePortalSession?.data?.url) {
        window.open(stripePortalSession.data.url, "_blank");
      }
    } catch (error) {
      console.error("Error fetching current plan:", error);
    } finally {
      setEaiButtonloading(false);
    }
  };

  // ====================== RENDER
  const renderEAISubtitle = () => {
    if (isEAICancellationRequested) {
      return `Cancels on ${currentEAIPlanEndDate}`;
    }
    if (isEAICancelManually) {
      return "Cancelled";
    }
    return `Renews on ${currentEAIPlanEndDate}`;
  };

  // ====================== BUTTON EVENTS
  const commonEAIPlanButtonEvent = {
    loading: eaiButtonLoading,
    disabled: eaiButtonLoading,
  };

  const renderEAIButton = (color) => {
    if (isEAIGiven) {
      return {
        text: "Complementary",
      };
    }

    return {
      buttonText:
        isEAICancellationRequested || isEAICancelManually
          ? "Resubscribe"
          : "Cancel subscription",
      buttonProps:
        isEAICancellationRequested || isEAICancelManually
          ? {
              variant: "outlined",
              startIcon: <AddIcon sx={{ color }} />,
              style: {
                borderRadius: "10px",
                borderColor: color,
              },
              sx: {
                "&:hover": {
                  backgroundColor: "#f4f4f4",
                },
              },
              ...commonEAIPlanButtonEvent,
            }
          : {
              variant: "text-animated",
              ...commonEAIPlanButtonEvent,
            },
    };
  };

  // ====================== PLAN MAP
  const EAIActionMap = {
    Trial: {
      title: EAICurrentPlanName,
      subTitle: `Trial expires on ${currentEAIPlanEndDate}`,
      buttonText: "Upgrade",
      buttonProps: {
        variant: "outlined",
        startIcon: <AddIcon />,
        style: {
          borderRadius: "10px",
        },
        ...commonEAIPlanButtonEvent,
      },
      cardStyle: {
        background:
          "linear-gradient(to top left, rgba(240, 240, 240, 0.6), rgba(255, 255, 255, 1))",
      },
    },
    "Member": {
      title: EAICurrentPlanName,
      subTitle: "Limited features",
      buttonText: "Upgrade",
      buttonProps: {
        variant: "outlined",
        startIcon: <AddIcon />,
        style: {
          borderRadius: "10px",
        },
        ...commonEAIPlanButtonEvent,
      },
      cardStyle: {
        background:
          "linear-gradient(to top left, rgba(240, 240, 240, 0.6), rgba(255, 255, 255, 1))",
      },
    },
    Starter: {
      title: EAICurrentPlanName,
      subTitle: renderEAISubtitle(),
      cardStyle: {
        background: "linear-gradient(to top left, #f4f4f4, #ffffff)",
      },
      color: "var(--color-primary)",
      ...renderEAIButton("var(--color-primary)"),
    },
    Growth: {
      title: EAICurrentPlanName,
      subTitle: renderEAISubtitle(),
      cardStyle: {
        background: "linear-gradient(to top left, #f4f4f4, #ffffff)",
      },
      color: "var(--color-primary)",
      ...renderEAIButton("var(--color-primary)"),
    },
    Premium: {
      title: EAICurrentPlanName,
      subTitle: renderEAISubtitle(),
      cardStyle: {
        background: "linear-gradient(to top left, #fdf5e6, #ffffff)",
      },
      color: "#D79F3F",
      ...renderEAIButton("#D79F3F"),
    },
    Failed: {
      title: EAICurrentPlanName,
      subTitle: "Payment failed",
      cardStyle: {
        background:
          "linear-gradient(to top left, rgba(255, 0, 0, 0.1), rgba(255, 0, 0, 0))",
      },
      color: "#D61F1F",
      buttonText: "Renew subscription",
      buttonProps: {
        variant: "text-animated",
        ...commonEAIPlanButtonEvent,
      },
    },
  };

  // ====================== VARIABLES
  const EAIPlanCard =
    EAIActionMap[isEAIPaymentFailed ? "Failed" : EAICurrentPlan];

  // ====================== RETURN
  return (
    <Box
      gridColumn={{ xs: "span 12", lg: "span 8" }}
      height="100%"
      width={"100%"}
    >
      <Stack
        border="1px solid #E0E2E4"
        borderRadius={"12px"}
        py={3}
        px={3}
        backgroundColor="#FFF"
        height={"100%"}
      >
        <Box pb={2}>
          <Typography color="#1F292E" fontSize={"1.15rem"} fontWeight={500}>
            Current Plan
          </Typography>
        </Box>
        <Stack direction={"row"} gap={3} width={"100%"} height={"100%"}>
          <PlanCard
            title={`${EAIPlanCard?.title} Plan`}
            plan={EAIPlan?.plan}
            isTrial={isTrial}
            loading={!EAIPlan}
            subTitle={EAIPlanCard?.subTitle}
            style={{
              ...EAIPlanCard?.cardStyle,
              border: "0.5px solid rgba(0, 0, 0, 0.10)",
            }}
            icon={
              <CreditCardIcon
                style={{
                  width: "1.875rem",
                  height: "1.875rem",
                  color: EAIPlanCard?.color,
                }}
              />
            }
            color={EAIPlanCard?.color}
            text={EAIPlanCard?.text}
            buttonText={EAIPlanCard?.buttonText}
            buttonProps={EAIPlanCard?.buttonProps}
            onClick={onHandleEAIPlanClick}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default CurrentPlanCard;
